import { useEffect, useRef } from "react";

/** Refresh mounted staff views without reloading the page or running hidden-tab work.
 * Callbacks must use the supplied signal and check it before committing state.
 * Existing authorised GETs only; no confirmation, send or workflow mutations.
 */
export function useLiveRefresh(refresh, { refreshKey, enabled = true, intervalMs = 5000 } = {}) {
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let timer;
    let running;
    const schedule = () => {
      clearTimeout(timer);
      if (!stopped && document.visibilityState !== "hidden") timer = setTimeout(run, intervalMs);
    };
    const run = async () => {
      if (stopped || running || document.visibilityState === "hidden") return;
      clearTimeout(timer);
      const controller = new AbortController();
      running = controller;
      try { await latest.current(controller.signal); }
      catch { /* Keep the last successful view; the next visible refresh retries. */ }
      finally { running = null; schedule(); }
    };
    const resume = () => {
      if (document.visibilityState === "hidden") { clearTimeout(timer); return; }
      run();
    };
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    schedule();
    return () => {
      stopped = true;
      clearTimeout(timer);
      running?.abort();
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [refreshKey, enabled, intervalMs]);
}
